import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { CihazAktivasyonSocketService } from './services/cihaz-aktivasyon-socket.service';
import { KartWebSocketService, ICihazKomutVerisi } from '@uygulamalar/hmi-pano/kart-okuyucu/services/kart-websocket.service';
import { clearCihazKayit, getCihazDurum, setCihazDurum } from '../../guards/cihaz.guard';
import { FikoYTIstekKok } from '@/src/service/modules/fikoyt-istek-kok/fikoyt.istek.makinesi.service';
import { hmiAyarGetir } from './config/hmi-parametre-setleri';
import { FikoYTKokBilesenComponent } from '@service/kok-bilesen/kok-bilesen.component';

@Component({
  selector: 'hmi-pano-shell',
  standalone: true,
  imports: [CommonModule, RouterModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './hmi-pano-shell.component.html',
  styleUrl: './hmi-pano-shell.component.less',
})
export class HmiPanoShellComponent extends FikoYTKokBilesenComponent implements OnInit, OnDestroy {
  private aktivasyonSocket = inject(CihazAktivasyonSocketService);
  private wsService = inject(KartWebSocketService);
  tarih = '';
  saat = '';
  wsConnected = true;
  bataryaSeviye = 100;
  bataryaSarj = true;
  bataryaDestekleniyor = false;
  karanlikMod = false;
  tamEkran = false;

  /** Uzak komutla gosterilen ekran mesaji */
  ekranMesaji = '';
  ekranMesajiRenk = '#1890ff';
  ekranMesajiGoster = false;

  /** Cihaz askıya alındı mı? */
  cihazAskida = false;

  private _timer: any;
  private _heartbeatTimer: any;
  private _durumPollingTimer: any;
  private _batarya: any;
  private _socketSub: Subscription | null = null;
  private _ekranMesajTimer: any;
  private _sonBilinenDurum: string = 'aktif';
  private _baslamaZamani = Date.now();
  private _destroy$ = new Subject<void>();
  private readonly TEMA_KEY = 'hmi-karanlik-mod';
  private readonly CIHAZ_KEY = 'fikoyt_cihaz_kimlik';

  // Olay sayaclari (telemetri ile gonderilir)
  private _olaylar = { kartOkuma: 0, komutAlma: 0, hata: 0 };

  constructor() { super(); }

  ngOnInit() {
    // Parametre sisteminden veya eski localStorage'dan tema yukle
    this.karanlikMod = hmiAyarGetir('karanlikMod', false) || localStorage.getItem(this.TEMA_KEY) === '1';
    if (this.karanlikMod) {
      document.body.classList.add('hmi-karanlik');
      document.documentElement.classList.add('tema-karanlik');
      document.body.classList.add('tema-karanlik');
    }

    // Parametre sisteminden tam ekran tercihi
    if (hmiAyarGetir('tamEkran', false)) {
      document.documentElement.requestFullscreen?.().catch(() => {});
    }

    // Sayfa yüklendiğinde askıda durumunu kontrol et
    const mevcutDurum = getCihazDurum();
    if (mevcutDurum === 'askida') {
      this.cihazAskida = true;
    }

    this.updateTime();
    this._timer = setInterval(() => {
      this.updateTime();
      this.CD.detectChanges();
    }, 1000);
    this.initBattery();
    this.cihazDurumDinle();
    this.cihazDurumPollingBaslat();
    this.telemetriBaslat();
    this.komutDinleyiciBaslat();

    // Olay loglama parametre ile kontrol edilir
    if (hmiAyarGetir('olayLoglama', true)) {
      this.olayDinleyiciBaslat();
    }

    // Baslangic olayi logla
    this.olayLogla('hmi_basladi', 'HMI Pano baslatildi');
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this._destroy$.next();
    this._destroy$.complete();
    if (this._timer) clearInterval(this._timer);
    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
    if (this._durumPollingTimer) clearInterval(this._durumPollingTimer);
    if (this._ekranMesajTimer) clearTimeout(this._ekranMesajTimer);
    if (this._batarya) {
      this._batarya.removeEventListener('levelchange', this._bataryaGuncelle);
      this._batarya.removeEventListener('chargingchange', this._bataryaGuncelle);
    }
    this._socketSub?.unsubscribe();

    // Kopma bildirimi
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY);
    if (cihazKimlik) {
      this.olayLogla('hmi_kapandi', 'HMI Pano kapatildi');
      this.FikoYT.io?.emit('FikoYT.sistem.io.cihaz.koptu', {
        cihaz_kimlik: cihazKimlik,
        kopma_zamani: new Date().toISOString(),
        uptime: Math.floor((Date.now() - this._baslamaZamani) / 1000),
      });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // UZAK KOMUT SISTEMI
  // ═══════════════════════════════════════════════════════════════════════════

  /** Admin panelden gelen uzak komutlari dinle ve calistir */
  private komutDinleyiciBaslat() {
    // Parametre sistemi: uzak komut kabul kontrolu
    if (!hmiAyarGetir('uzakKomutKabul', true)) return;

    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY);
    if (!cihazKimlik) return;

    this.wsService.komutGeldi$
      .pipe(takeUntil(this._destroy$))
      .subscribe(komut => {
        // Sadece bu cihaza gelen komutlari isle
        if (komut.cihaz_kimlik && komut.cihaz_kimlik !== cihazKimlik) return;
        this._olaylar.komutAlma++;
        this.olayLogla('komut_alindi', `Uzak komut: ${komut.komut_kodu}`);
        this.komutuCalistir(komut);
      });
  }

  /** Komut calistirma motoru */
  private komutuCalistir(komut: ICihazKomutVerisi) {
    const kod = komut.komut_kodu;
    const params = komut.parametreler || {};
    let basarili = false;
    let mesaj = '';

    try {
      switch (kod) {
        // ─── YENIDEN BASLAT (sayfa yenile) ───
        case 'yeniden_basla':
          basarili = true;
          mesaj = 'Sayfa 2 saniye icinde yenilenecek';
          this.komutCevabiGonder(komut, basarili, mesaj);
          this.olayLogla('yeniden_baslatiliyor', 'Uzak komutla sayfa yenileniyor');
          setTimeout(() => window.location.reload(), 2000);
          return; // Cevap zaten gonderildi

        // ─── EKRAN MESAJI ───
        case 'ekran_mesaji':
          const ekranMesaj = params['mesaj'] || 'Mesaj yok';
          const sure = (params['sure'] || 10) * 1000;
          const renk = params['renk'] || '#1890ff';
          this.ekranMesajiGoster = true;
          this.ekranMesaji = ekranMesaj;
          this.ekranMesajiRenk = renk;
          this.CD.detectChanges();
          if (this._ekranMesajTimer) clearTimeout(this._ekranMesajTimer);
          this._ekranMesajTimer = setTimeout(() => {
            this.ekranMesajiGoster = false;
            this.CD.detectChanges();
          }, sure);
          basarili = true;
          mesaj = `Mesaj gosterildi (${sure / 1000}sn): ${ekranMesaj}`;
          break;

        // ─── KONFIGURASYON GONDER ───
        case 'konfig_gonder':
          try {
            const konfig = typeof params['konfig'] === 'string'
              ? JSON.parse(params['konfig'])
              : params['konfig'];
            // Konfigurasyon uygula
            if (konfig?.cihaz_ismi) {
              localStorage.setItem('fikoyt_cihaz_ismi', konfig.cihaz_ismi);
            }
            if (konfig?.karanlik_mod !== undefined) {
              this.karanlikMod = !!konfig.karanlik_mod;
              document.body.classList.toggle('hmi-karanlik', this.karanlikMod);
              document.documentElement.classList.toggle('tema-karanlik', this.karanlikMod);
              document.body.classList.toggle('tema-karanlik', this.karanlikMod);
              localStorage.setItem(this.TEMA_KEY, this.karanlikMod ? '1' : '0');
            }
            if (konfig?.tam_ekran !== undefined && konfig.tam_ekran) {
              this.toggleTamEkran();
            }
            basarili = true;
            mesaj = 'Konfigurasyon basariyla uygulandi';
          } catch (e: any) {
            basarili = false;
            mesaj = 'Konfigurasyon parse hatasi: ' + (e?.message || 'bilinmeyen');
          }
          break;

        // ─── LED YAK (ekranda flash efekti) ───
        case 'led_yak':
          const ledRenk = params['renk'] || '#00ff00';
          const ledSure = params['sure'] || 3000;
          document.body.style.transition = 'box-shadow 0.3s';
          document.body.style.boxShadow = `inset 0 0 100px ${ledRenk}`;
          setTimeout(() => {
            document.body.style.boxShadow = 'none';
          }, ledSure);
          basarili = true;
          mesaj = `LED efekti gosterildi (${ledRenk}, ${ledSure}ms)`;
          break;

        // ─── LOG TOPLA ───
        case 'log_topla':
          const logVeri = {
            uptime_sn: Math.floor((Date.now() - this._baslamaZamani) / 1000),
            sayfa_url: window.location.href,
            user_agent: navigator.userAgent,
            ekran: { w: window.innerWidth, h: window.innerHeight, tamEkran: this.tamEkran },
            batarya: { seviye: this.bataryaSeviye, sarj: this.bataryaSarj },
            ag: { cevrimici: navigator.onLine, tur: (navigator as any).connection?.effectiveType },
            bellek: (performance as any).memory ? {
              kullanilan: Math.round((performance as any).memory.usedJSHeapSize / 1024 / 1024),
              toplam: Math.round((performance as any).memory.totalJSHeapSize / 1024 / 1024),
              limit: Math.round((performance as any).memory.jsHeapSizeLimit / 1024 / 1024),
            } : null,
            karanlik_mod: this.karanlikMod,
            olaylar: { ...this._olaylar },
            cihaz_ismi: localStorage.getItem('fikoyt_cihaz_ismi') || 'HMI Pano',
          };
          basarili = true;
          mesaj = JSON.stringify(logVeri);
          break;

        // ─── FIRMWARE GUNCELLE (zorla yenile) ───
        case 'firmware_guncelle':
          basarili = true;
          mesaj = 'Cache temizlenip sayfa yenilenecek';
          this.komutCevabiGonder(komut, basarili, mesaj);
          this.olayLogla('firmware_guncelleme', 'Cache temizleniyor ve yenileniyor');
          // Cache'i atla ve hard reload
          setTimeout(() => {
            const w = window as any;
            if (w.caches) {
              w.caches.keys().then((names: string[]) => {
                for (const name of names) w.caches.delete(name);
              }).finally(() => w.location.reload());
            } else {
              w.location.reload();
            }
          }, 2000);
          return;

        // ─── DURUM SORGULA ───
        case 'durum_sorgula':
          basarili = true;
          mesaj = JSON.stringify({
            durum: 'aktif',
            uptime_sn: Math.floor((Date.now() - this._baslamaZamani) / 1000),
            karanlik_mod: this.karanlikMod,
            tam_ekran: this.tamEkran,
            batarya: this.bataryaSeviye,
            cevrimici: navigator.onLine,
          });
          break;

        default:
          basarili = false;
          mesaj = `Bilinmeyen komut: ${kod}`;
          this._olaylar.hata++;
      }
    } catch (e: any) {
      basarili = false;
      mesaj = `Komut calistirma hatasi: ${e?.message || 'bilinmeyen'}`;
      this._olaylar.hata++;
    }

    this.komutCevabiGonder(komut, basarili, mesaj);
    this.olayLogla(basarili ? 'komut_tamamlandi' : 'komut_hata', `${kod}: ${mesaj}`);
  }

  /** Komut cevabini Socket.IO uzerinden gonder */
  private komutCevabiGonder(komut: ICihazKomutVerisi, basarili: boolean, mesaj: string) {
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY) || '';
    this.wsService.komutCevabiGonder({
      komut_id: komut._id,
      komut_kodu: komut.komut_kodu,
      cihaz_kimlik: cihazKimlik,
      basarili,
      mesaj,
      durum: basarili ? 'tamamlandi' : 'hata',
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // OLAY LOGLAMA SISTEMI
  // ═══════════════════════════════════════════════════════════════════════════

  /** Olay logla — sunucuya gonderilir, DB'ye kaydedilir, admin panelde gorulur */
  private olayLogla(olayKodu: string, aciklama: string, ekVeri?: any) {
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY) || '';
    if (!cihazKimlik) return;

    this.wsService.olayGonder({
      cihaz_kimlik: cihazKimlik,
      cihaz_tipi: 'hmi_pano',
      olay_kodu: olayKodu,
      aciklama,
      kaynak: 'hmi_pano',
      veri: ekVeri,
    });
  }

  /** HMI kart olaylarini dinle ve logla */
  private olayDinleyiciBaslat() {
    // Kart olaylarini logla
    this.wsService.kartOlayi$
      .pipe(takeUntil(this._destroy$))
      .subscribe(olay => {
        this._olaylar.kartOkuma++;
        this.olayLogla(
          olay.durum === 'koyuldu' ? 'kart_koyuldu' : 'kart_cekildi',
          `Kart ${olay.durum}: ${olay.kartKimlik}`,
          { kartKimlik: olay.kartKimlik, cihazKimlik: olay.cihazKimlik },
        );

        const cihazOlay = {
          cihaz_kimlik: olay.cihazKimlik,
          olay_kodu: olay.durum === 'koyuldu' ? 'kart_koyuldu' : 'kart_cekildi',
          kartKimlik: olay.kartKimlik,
        };
        this.FikoYT.io?.emit('FikoYT.sistem.io.cihaz.olay', cihazOlay);

        // Kart çekildi → sadece HMI pano içindeyken ana ekrana dön
        if (olay.durum === 'cekildi' && this.router.url.includes('/hmi-pano/')) {
          this.ngZone.run(() => {
            this.router.navigate(['/hmi-pano/pano']);
          });
        }
      });

    // Ag durumu degisikliklerini logla
    const onlineH = () => this.olayLogla('ag_baglandi', 'Internet baglantisi kuruldu');
    const offlineH = () => {
      this._olaylar.hata++;
      this.olayLogla('ag_koptu', 'Internet baglantisi kesildi');
    };
    window.addEventListener('online', onlineH);
    window.addEventListener('offline', offlineH);
    this._destroy$.subscribe(() => {
      window.removeEventListener('online', onlineH);
      window.removeEventListener('offline', offlineH);
    });

    // Hata yakalayici
    const errorH = (e: ErrorEvent) => {
      this._olaylar.hata++;
      this.olayLogla('js_hata', `${e.message} (${e.filename}:${e.lineno})`);
    };
    window.addEventListener('error', errorH);
    this._destroy$.subscribe(() => window.removeEventListener('error', errorH));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CIHAZ DURUM & TELEMETRI
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Socket.IO uzerinden cihaz durumu degisikliklerini dinle.
   * Cift katmanli: 1) CihazAktivasyonSocketService, 2) Dogrudan socket dinleme (yedek)
   */
  private cihazDurumDinle() {
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY);
    if (!cihazKimlik) {
      console.warn('[HMI Shell] cihazKimlik bulunamadi, durum dinleme baslatilmadi');
      return;
    }

    console.log('[HMI Shell] Cihaz durum dinleme baslatiliyor, kimlik:', cihazKimlik);

    // 1. Katman: CihazAktivasyonSocketService uzerinden
    this.aktivasyonSocket.dinlemeBasla();
    this._socketSub = this.aktivasyonSocket
      .cihazOlaylari$(cihazKimlik)
      .subscribe(olay => {
        console.log('[HMI Shell] Aktivasyon olayi alindi (servis):', olay.tip, olay.durum);
        if (olay.tip === 'aktivasyon_durum_degisti') {
          this.durumDegisikligiUygula(olay.durum || '');
        }
      });

    // 2. Katman: Dogrudan socket dinleme (yedek — servis calismasa bile yakala)
    const socket = this.FikoYT.io;
    if (socket) {
      const sub = socket.fromEvent('FikoYT.sistem.io.cihaz.guncellendi')
        .pipe(takeUntil(this._destroy$))
        .subscribe((data: any) => {
          const payload = Array.isArray(data) ? data[0] : data;
          if (!payload || typeof payload !== 'object') return;
          if (payload.tip !== 'aktivasyon_durum_degisti') return;
          if (payload.cihaz_kimlik !== cihazKimlik) return;

          console.log('[HMI Shell] Aktivasyon olayi alindi (dogrudan socket):', payload.durum);
          this.durumDegisikligiUygula(payload.durum || '');
        });
    } else {
      console.warn('[HMI Shell] Socket.IO baglantisi bulunamadi, dogrudan dinleme baslatilmadi');
    }
  }

  /** Durum degisikligi islemleri (iptal/askida/aktif) */
  private durumDegisikligiUygula(durum: string) {
    this.ngZone.run(() => {
      if (durum === 'iptal') {
        this.olayLogla('cihaz_deaktif', 'Cihaz iptal edildi');
        clearCihazKayit();
        this.nzMessage.warning('Cihaz kaydı yönetici tarafından iptal edildi');
        this.router.navigate(['/hmi-pano/aktivasyon']);
      } else if (durum === 'askida') {
        if (this.cihazAskida) return; // Zaten askida
        this.olayLogla('cihaz_askida', 'Cihaz askıya alındı');
        setCihazDurum('askida');
        this.cihazAskida = true;
        this.CD.detectChanges();
      } else if (durum === 'aktif') {
        // Askidayken aktif oldu — sayfa reload ile temiz baslat
        if (this.cihazAskida || this._sonBilinenDurum === 'askida') {
          this.olayLogla('cihaz_aktif', 'Cihaz tekrar aktif edildi, sayfa yenileniyor');
          setCihazDurum('aktif');
          window.location.reload();
          return;
        }
        setCihazDurum('aktif');
        this.cihazAskida = false;
        this.CD.detectChanges();
      }
    });
  }

  /**
   * Socket.IO kesilirse tek seferlik cihaz durum kontrolu.
   * Artık periyodik polling yok — Socket.IO ile dinleniyor.
   */
  private cihazDurumPollingBaslat() {
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY);
    if (!cihazKimlik) return;

    this._sonBilinenDurum = getCihazDurum() || 'aktif';

    // Sayfa açılışında tek seferlik kontrol (5sn sonra)
    setTimeout(() => this.cihazDurumKontrolEt(cihazKimlik), 5000);
  }

  private _pollingBasarisizSayac = 0;

  private async cihazDurumKontrolEt(cihazKimlik: string) {
    try {
      const istek = new FikoYTIstekKok({
        metod: 'POST',
        komut: 'FikoYT.sistem.DB.hareketler.aggregate',
      });
      istek.parametreler = {
        aggregate: [
          { $match: { vt: 'FikoYT.sistem.cihazlar' } },
          { $match: { cihazKimlik } },
          { $sort: { _id: -1 } },
          { $limit: 1 },
          { $project: { aktivasyonDurumu: 1 } },
        ],
      };
      await istek.isteSync();
      const sonuc = Array.isArray(istek.cevap?.sonuc) ? istek.cevap.sonuc : [];

      const cihaz = sonuc[0];

      if (!cihaz) {
        // Cihaz bulunamadi — sayacı artır ama agresif silme yapma
        this._pollingBasarisizSayac++;
        console.warn('[HMI Shell] Polling: cihaz bulunamadi, sayac:', this._pollingBasarisizSayac);
        // 20 ardışık başarısız sorgu (5dk) sonra iptal — geçici ağ sorunlarını önle
        if (this._pollingBasarisizSayac >= 20 && this._sonBilinenDurum !== 'iptal') {
          console.error('[HMI Shell] Polling: 20 ardisik basarisiz, cihaz iptal ediliyor');
          this._sonBilinenDurum = 'iptal';
          this.durumDegisikligiUygula('iptal');
        }
        return;
      }

      // Basarili sorgu — sayaci sifirla
      this._pollingBasarisizSayac = 0;
      const yeniDurum = cihaz.aktivasyonDurumu || 'aktif';

      // Durum degisti mi?
      if (yeniDurum !== this._sonBilinenDurum) {
        console.log('[HMI Shell] Polling: durum degisti', this._sonBilinenDurum, '→', yeniDurum);
        this._sonBilinenDurum = yeniDurum;
        this.durumDegisikligiUygula(yeniDurum);
      }
    } catch (e) {
      // Ag hatasi — sessizce atla, sonraki polling'de tekrar dener
      console.warn('[HMI Shell] Polling: ag hatasi, atlanıyor');
    }
  }

  /**
   * Telemetri heartbeat baslat.
   * Her 30 saniyede bir cihaz durumunu Socket.IO ile admin paneline bildirir.
   */
  private telemetriBaslat() {
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY);
    if (!cihazKimlik) return;

    // Baglanti bildirimi — desteklenen komutlarla birlikte
    let cihazDbId = '';
    try { cihazDbId = JSON.parse(localStorage.getItem('fikoyt_cihaz_konfig') || '{}')?._id || ''; } catch {}
    this.FikoYT.io?.emit('FikoYT.sistem.io.cihaz.baglandi', {
      cihaz_kimlik: cihazKimlik,
      cihaz_db_id: cihazDbId,
      cihaz_ismi: localStorage.getItem('fikoyt_cihaz_ismi') || 'HMI Pano',
      tur: 'hmi_pano',
      cihaz_tipi: 'hmi_pano',
      baglanti_zamani: new Date().toISOString(),
      firmware_surum: '2.0.0',
      ip_adres: window.location.hostname,
      user_agent: navigator.userAgent,
      desteklenen_komutlar: [
        'yeniden_basla', 'ekran_mesaji', 'konfig_gonder',
        'led_yak', 'log_topla', 'firmware_guncelle', 'durum_sorgula',
      ],
    });

    // Ilk telemetri hemen gonder
    setTimeout(() => this.telemetriGonder(), 2000);

    // Parametre sisteminden telemetri araligi (varsayilan 30sn)
    const telemetriAraligi = hmiAyarGetir('telemetriAraligi', 30) * 1000;
    this._heartbeatTimer = setInterval(() => this.telemetriGonder(), telemetriAraligi);
  }

  /** Anlik telemetri verisini Socket.IO uzerinden gonder */
  private telemetriGonder() {
    const cihazKimlik = localStorage.getItem(this.CIHAZ_KEY);
    if (!cihazKimlik) return;

    const connection = (navigator as any).connection;
    const perf = (performance as any).memory;

    this.FikoYT.io?.emit('FikoYT.sistem.io.cihaz.telemetri', {
      tip: 'telemetri',
      cihaz_kimlik: cihazKimlik,
      cihaz_ismi: localStorage.getItem('fikoyt_cihaz_ismi') || 'HMI Pano',
      zaman: new Date().toISOString(),
      batarya: {
        seviye: this.bataryaSeviye,
        sarj: this.bataryaSarj,
        destekleniyor: this.bataryaDestekleniyor,
      },
      ag: {
        cevrimici: navigator.onLine,
        tur: connection?.effectiveType || 'wifi',
        downlink: connection?.downlink,
        rtt: connection?.rtt,
      },
      ekran: {
        genislik: window.innerWidth,
        yukseklik: window.innerHeight,
        tamEkran: this.tamEkran,
        pikselOrani: window.devicePixelRatio,
      },
      sistem: {
        user_agent: navigator.userAgent,
        platform: navigator.platform,
        dil: navigator.language,
        firmware: '2.0.0',
        bellek: perf ? {
          kullanilan_mb: Math.round(perf.usedJSHeapSize / 1024 / 1024),
          toplam_mb: Math.round(perf.totalJSHeapSize / 1024 / 1024),
          limit_mb: Math.round(perf.jsHeapSizeLimit / 1024 / 1024),
        } : null,
      },
      istatistik: {
        kart_okuma: this._olaylar.kartOkuma,
        komut_alma: this._olaylar.komutAlma,
        hata: this._olaylar.hata,
      },
      karanlikMod: this.karanlikMod,
      uptime: Math.floor((Date.now() - this._baslamaZamani) / 1000),
    });
  }

  private _bataryaGuncelle = () => {
    if (!this._batarya) return;
    this.bataryaSeviye = Math.round(this._batarya.level * 100);
    this.bataryaSarj = this._batarya.charging;
    this.CD.detectChanges();
  };

  private initBattery() {
    if (!(navigator as any).getBattery) return;
    (navigator as any).getBattery().then((bat: any) => {
      this._batarya = bat;
      this.bataryaDestekleniyor = true;
      this._bataryaGuncelle();
      bat.addEventListener('levelchange', this._bataryaGuncelle);
      bat.addEventListener('chargingchange', this._bataryaGuncelle);
    }).catch(() => {});
  }

  toggleKaranlikMod() {
    this.karanlikMod = !this.karanlikMod;
    document.body.classList.toggle('hmi-karanlik', this.karanlikMod);
    document.documentElement.classList.toggle('tema-karanlik', this.karanlikMod);
    document.body.classList.toggle('tema-karanlik', this.karanlikMod);
    localStorage.setItem(this.TEMA_KEY, this.karanlikMod ? '1' : '0');
    this.CD.detectChanges();
  }

  async toggleTamEkran() {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        this.tamEkran = true;
        try { await (screen.orientation as any).lock('landscape'); } catch (_) {}
      } else {
        await document.exitFullscreen();
        this.tamEkran = false;
      }
      this.CD.detectChanges();
    } catch (_) {}
  }

  private updateTime() {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    this.tarih = `${pad(now.getDate())}.${pad(now.getMonth() + 1)}.${now.getFullYear()}`;
    this.saat = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  }
}
